import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import CompanyLogo from './CompanyLogo';
import FeatureSpotlight, { useFeatureSpotlight } from './FeatureSpotlight';
import { HomeIcon, ExternalLink, ChevronRight } from 'lucide-react';

// Small live dot per casa: green = the house answers through its tunnel (its HA,
// or the UniFi controller for infra-only houses — via:'unifi'), red = it should
// answer and doesn't, gray = still checking or nothing to probe (no_domotics).
// Reuses the smart-homes /live endpoint (admin-only).
function LiveDot({ companyId }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['smart-home-live', companyId],
    queryFn: () => api.get(`/smart-homes/${companyId}/live`).then((r) => r.data).catch(() => ({ ok: false })),
    refetchInterval: 120000,
    staleTime: 60000,
  });
  const noProbe = data != null && !data.ok && data.error === 'no_domotics';
  const color = data == null || noProbe ? 'var(--c-text-muted)' : data.ok ? '#16a34a' : '#ef4444';
  const title = data == null ? '…'
    : noProbe ? t('dashboard.managedHomes.dotNoProbe')
    : data.ok ? (data.via === 'unifi' ? t('dashboard.managedHomes.dotUnifiOnline') : `HA ${data.version || ''} online`)
    : t('dashboard.managedHomes.dotOffline');
  return <span title={title} style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />;
}

// Admin-only quick-access strip at the top of the dashboard: every managed
// Home Assistant (Casas Managed roster — includes the BrandPlus video wall)
// with its live status and, when a public URL exists, a one-click "open HA".
export default function ManagedHomesStrip() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spot = useFeatureSpotlight('dashboard_managed_homes');

  const { data } = useQuery({
    queryKey: ['smart-homes-list'],
    queryFn: () => api.get('/smart-homes/list').then((r) => r.data).catch(() => ({ casas: [] })),
    refetchInterval: 300000,
  });
  const casas = data?.casas || [];
  if (!casas.length) return null;

  return (
    <>
      <div
        className="card"
        ref={spot.anchorRef}
        style={{
          marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 16px', borderLeft: '3px solid var(--c-brand)', flexWrap: 'wrap',
        }}
      >
        <HomeIcon size={20} style={{ color: 'var(--c-brand)', flexShrink: 0 }} />
        <div style={{ fontWeight: 600, fontSize: 14, flexShrink: 0 }}>{t('dashboard.managedHomes.title')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          {casas.map((casa) => (
            <span
              key={casa.id}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5,
                padding: '4px 10px', background: 'var(--c-surface-2)', borderRadius: 999,
                cursor: 'pointer', maxWidth: 240,
              }}
              onClick={() => (casa.open_url ? window.open(casa.open_url, '_blank', 'noopener') : navigate('/smart-homes'))}
              title={casa.open_url ? t('dashboard.managedHomes.openHint') : t('dashboard.managedHomes.detailHint')}
            >
              <CompanyLogo name={casa.name} logoUrl={casa.logo_url} size={18} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{casa.name}</span>
              <LiveDot companyId={casa.id} />
              {casa.open_url && <ExternalLink size={12} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />}
            </span>
          ))}
        </div>
        <span
          onClick={() => navigate('/smart-homes')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 12, color: 'var(--c-brand)', cursor: 'pointer', flexShrink: 0 }}
        >
          {t('dashboard.managedHomes.viewAll')} <ChevronRight size={14} />
        </span>
      </div>

      <FeatureSpotlight
        anchorRef={spot.anchorRef}
        show={spot.shouldShow}
        onDismiss={spot.dismiss}
        title={t('dashboard.managedHomes.spotTitle')}
        body={t('dashboard.managedHomes.spotBody')}
        placement="bottom"
      />
    </>
  );
}